import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronDown,
	faTools,
	faCar,
	faTrash,
	faCircle,
	faStar,
} from "@fortawesome/free-solid-svg-icons"
import moment from "moment"
import { useGroups } from "../../hooks/groups"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { COLORS } from "../../utilities/Constants"
import { formatNumber } from "../../utilities/Addons"
import { depTable } from "../PDF/DepTable"
import {
	deleteDepannage,
	deleteDepannageReset,
} from "../../store/actions/depSearchActions"
import DepSearchTotal from "./DepSearchTotal"
import ResultDeleteModal from "../others/ResultDeleteModal"
import ResultNumCell, { RESULT_ICON_SIZE } from "../others/ResultNumCell"
import { fetchFichesOuvertes } from "../../store/actions/ficheOuverteActions"
import { detenteurFiche } from "../../hooks/useFicheVerrou"
import { setFeuilletage } from "../../store/actions/feuilletageActions"
import { getOpenerId } from "../../utilities/session"

// Résultats de recherche dépannage (V2). Page 2/2. Style calqué sur
// AtelierResultatsV2 (conteneur arrondi + ombre, zebra + hover, tri par colonne).
// Deux tableaux : Dépannages (triable) + Livraisons (comme le legacy). Double-clic
// ouvre `/depannage/fiche/:id`.
const byDateTime = (a, b) =>
	moment(a.date + " " + a.heure, "DD-MM-YYYY kk:mm") -
	moment(b.date + " " + b.heure, "DD-MM-YYYY kk:mm")

const COMPARATORS = {
	date: byDateTime,
	depanneur: (a, b) =>
		(a.employe || "").localeCompare(b.employe || "", "fr") || byDateTime(a, b),
	nom: (a, b) => (a.nom || "").localeCompare(b.nom || "", "fr") || byDateTime(a, b),
	appel: (a, b) =>
		(a.appel || "").localeCompare(b.appel || "", "fr") || byDateTime(a, b),
	total: (a, b) => Number(a.totalFinal) - Number(b.totalFinal),
}
// Mapping vers l'ordre serveur utilisé par la re-recherche après suppression.
const ORDER_BY = {
	date: "date",
	depanneur: "depanneur",
	nom: "nom",
	appel: "appel",
	total: "total",
}

const SortableTh = ({ label, colKey, sort, onSort, right }) => {
	const active = sort.key === colKey
	return (
		<th style={right ? tableStyles.thRight : tableStyles.th}>
			<span style={styles.thInner}>
				{label}
				<button
					type="button"
					style={styles.sortBtn(active, sort.asc)}
					onClick={() => onSort(colKey)}
				>
					<FontAwesomeIcon icon={faChevronDown} />
				</button>
			</span>
		</th>
	)
}

// Cellule N° partagée (numéro, dossier vert, cadenas) + les deux statuts
// propres au dépannage : pastille « reste à payer » (remplace le fond
// cell-show du legacy) et étoile « fiche marquée » (remplace le fond jaune de
// ligne — cf. `rowBackground`, décision Jordan 31.07.2026 : aucun fond de
// ligne ne porte plus de sens).
const NumCell = ({ index, isRestePayer, isMarked, isOpen, lockedBy }) => (
	<ResultNumCell index={index} isOpen={isOpen} lockedBy={lockedBy}>
		{isMarked && (
			<FontAwesomeIcon
				icon={faStar}
				style={{ color: COLORS.warning, fontSize: RESULT_ICON_SIZE }}
				title="Fiche marquée"
			/>
		)}
		{isRestePayer && (
			<FontAwesomeIcon
				icon={faCircle}
				style={{ color: COLORS.danger, fontSize: 7 }}
				title="Reste à payer"
			/>
		)}
	</ResultNumCell>
)

// Boutons outils communs aux deux tableaux (atelier / location / suppression).
const ToolsCell = ({ dep, isBoss, onOpenAtelier, onOpenLocation, onDelete }) => {
	const stop = (fn) => (e) => {
		e.stopPropagation()
		fn()
	}
	return (
		<td style={styles.td}>
			<div style={styles.toolsCell}>
				{dep.atelier > 0 && (
					<button
						type="button"
						style={gs.iconBtn(COLORS.text)}
						title="Fiche atelier"
						onClick={stop(onOpenAtelier)}
					>
						<FontAwesomeIcon icon={faTools} />
					</button>
				)}
				{dep.location > 0 && (
					<button
						type="button"
						style={gs.iconBtn(COLORS.text)}
						title="Fiche location"
						onClick={stop(onOpenLocation)}
					>
						<FontAwesomeIcon icon={faCar} />
					</button>
				)}
				{isBoss && (
					<button
						type="button"
						style={gs.iconBtn(COLORS.danger)}
						title="Supprimer"
						onClick={stop(onDelete)}
					>
						<FontAwesomeIcon icon={faTrash} />
					</button>
				)}
			</div>
		</td>
	)
}

// Plus de fond de ligne porteur de sens : « marquée » est passée en étoile
// dans la cellule N° (cf. NumCell), la ligne garde zébrage + hover.
const rowBackground = (hover, index) =>
	hover ? tableStyles.rowHover : tableStyles.rowBg(index)

const DepRow = ({
	dep,
	index,
	montant,
	isBoss,
	tools,
	lockedBy,
	isOpen,
	onDoubleClick,
}) => {
	const [hover, setHover] = useState(false)
	return (
		<tr
			onDoubleClick={onDoubleClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: "pointer",
				backgroundColor: rowBackground(hover, index),
				transition: "background-color 0.12s",
			}}
		>
			<NumCell
				index={index}
				isRestePayer={dep.isRestePayer}
				isMarked={dep.marked}
				isOpen={isOpen}
				lockedBy={lockedBy}
			/>
			<td style={styles.td}>{dep.date}</td>
			<td style={styles.td}>{dep.heure}</td>
			<td style={styles.td}>{dep.employe}</td>
			<td style={styles.td}>{dep.nom}</td>
			<td style={styles.td}>{dep.appel}</td>
			<td style={styles.td}>{dep.lieu}</td>
			<td style={styles.td}>{dep.dest}</td>
			<td style={styles.td}>{dep.marque}</td>
			<td style={styles.td}>{dep.plaques}</td>
			<td style={{ ...styles.td, textAlign: "right" }}>{formatNumber(montant)}</td>
			<ToolsCell dep={dep} isBoss={isBoss} {...tools} />
		</tr>
	)
}

const LivRow = ({ dep, index, isBoss, tools, lockedBy, isOpen, onDoubleClick }) => {
	const [hover, setHover] = useState(false)
	return (
		<tr
			onDoubleClick={onDoubleClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: "pointer",
				backgroundColor: rowBackground(hover, index),
				transition: "background-color 0.12s",
			}}
		>
			<NumCell
				index={index}
				isRestePayer={dep.isRestePayer}
				isMarked={dep.marked}
				isOpen={isOpen}
				lockedBy={lockedBy}
			/>
			<td style={styles.td}>{dep.livDate}</td>
			<td style={styles.td}>{dep.livHeure}</td>
			<td style={styles.td}>{dep.livEmp}</td>
			<td style={styles.td}>{dep.nom}</td>
			<td style={styles.td}>{dep.appel}</td>
			<td style={styles.td}>{dep.lieu}</td>
			<td style={styles.td}>{dep.livInfo}</td>
			<td style={styles.td}>{dep.marque}</td>
			<td style={styles.td}>{dep.plaques}</td>
			<td style={{ ...styles.td, textAlign: "right" }}>
				{formatNumber(dep.livTot)}
			</td>
			<ToolsCell dep={dep} isBoss={isBoss} {...tools} />
		</tr>
	)
}

const DepResultatsV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	const { deps, livs, criteres, deleteDepannage: deleteState } = useSelector(
		(state) => state.depSearchInfo,
	)
	const openDepId = useSelector((state) => state.depInfo.dep.id)
	const fichesOuvertes = useSelector(
		(state) => state.fichesOuvertesInfo.fichesOuvertes,
	)
	// Dossier vert : les deux tableaux ouvrent la même fiche dépannage, la
	// dernière visitée s'y marque donc pareil (sentinelle 0 = aucune).
	const isDerniereVisitee = (ficheId) => openDepId > 0 && openDepId === ficheId

	// Cadenas : nom du détenteur si la fiche est verrouillée par une AUTRE
	// session (même règle et même affichage que les résultats atelier).
	const openerId = getOpenerId()
	const lockedBy = (ficheId) => {
		const detenteur = detenteurFiche(fichesOuvertes, "dep", ficheId, openerId)
		return detenteur ? detenteur.user : null
	}

	useEffect(() => {
		dispatch(fetchFichesOuvertes())
	}, [dispatch])

	const [sort, setSort] = useState({ key: "date", asc: true })
	const [openTotal, setOpenTotal] = useState(false)
	const [modalDelete, setModalDelete] = useState(null) // {id, title}

	// Ids des dépannages ayant une livraison → le montant affiché retranche livTot
	// (depTot du legacy).
	const livIds = useMemo(() => new Set((livs || []).map((l) => l.id)), [livs])
	const depMontant = (dep) =>
		livIds.has(dep.id) ? dep.totalFinal - dep.livTot : dep.totalFinal

	// Copie triée — on ne mute jamais le tableau redux (le legacy le faisait).
	const sortedDeps = useMemo(() => {
		const cmp = COMPARATORS[sort.key]
		const dir = sort.asc ? 1 : -1
		return cmp ? [...deps].sort((a, b) => dir * cmp(a, b)) : [...deps]
	}, [deps, sort])

	const onSort = (key) =>
		setSort((prev) =>
			prev.key === key ? { key, asc: !prev.asc } : { key, asc: true },
		)

	// `liste` = le tableau d'où l'on part (dépannages ou livraisons) : on fige
	// l'ORDRE AFFICHÉ pour le feuilletage Page Up/Down, ce qui remplace le
	// comptage manuel pgIndex/pgLength de l'ancienne fiche.
	const openFiche = (id, liste) => {
		dispatch(
			setFeuilletage(
				"dep",
				liste.map((d) => d.id),
				"depSearch",
			),
		)
		history.push(`/depannage/fiche/${id}`, { from: "depSearch" })
	}

	const doDelete = () => {
		// Le backend DELETE relance la recherche : même dialecte que le GET
		// (appel_type / num_dossier / vehicule_depannage), ids stringifiés, cases
		// en booléens.
		const search = {
			nom: criteres.nom || "",
			plaques: criteres.plaques || "",
			marque: criteres.marque || "",
			depanneur: String(criteres.depanneur ?? "0"),
			appel_type: String(criteres.appel_type ?? "-1"),
			num_dossier: criteres.num_dossier || "",
			matricule: criteres.matricule || "",
			vehicule_depannage: String(criteres.vehicule_depannage ?? "0"),
			secteur_police: String(criteres.secteur_police ?? "0"),
			assistance_entite: String(criteres.assistance_entite ?? "0"),
			etat: String(criteres.etat ?? "-1"),
			dateT: String(criteres.dateT ?? "0"),
			dateS: criteres.dateS || "",
			dateE: criteres.dateE || "",
			surPlace: criteres.surPlace === "on",
			marked: criteres.marked === "on",
			boss: criteres.boss === "on",
		}
		dispatch(
			deleteDepannage({
				id: modalDelete.id,
				search,
				orderBy: ORDER_BY[sort.key] || "",
			}),
		)
	}
	// Purge d'abord le slice : un état resté d'un flux interrompu (page quittée
	// en pleine suppression) rouvrirait le modal sur un ✓ ou une erreur périmés.
	const openDelete = (fiche) => {
		dispatch(deleteDepannageReset())
		setModalDelete(fiche)
	}
	const closeDelete = () => {
		setModalDelete(null)
		dispatch(deleteDepannageReset())
	}

	// Raccourcis repris du legacy : F10 → Total (boss), F11 → impression PDF,
	// Échap → accueil. F10/F11 inertes pendant la confirmation de suppression ;
	// Échap ne navigue pas par-dessus un modal (même règle que les fiches).
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "F10") {
				e.preventDefault()
				if (isBoss && deps.length > 0 && !modalDelete)
					setOpenTotal((v) => !v)
			} else if (e.key === "F11") {
				e.preventDefault()
				if (deps.length > 0 && !modalDelete) depTable(deps, livs)
			} else if (e.key === "Escape") {
				if (!modalDelete && !openTotal) history.push("/")
			}
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [isBoss, deps, livs, history, modalDelete, openTotal])

	const rowTools = (dep) => ({
		onOpenAtelier: () => history.push(`/atelier/fiche/${dep.atelier}`),
		onOpenLocation: () => history.push(`/location/fiche/${dep.location}`),
		onDelete: () =>
			openDelete({
				id: dep.id,
				title: `Dépannage du ${dep.date} à ${dep.heure}`,
			}),
	})

	return (
		<div style={gs.page}>
			<div style={styles.shell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Dépannage</div>
						<div style={gs.title}>Résultats ({deps.length})</div>
					</div>
					<div style={styles.headerActions}>
						{deps.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => depTable(deps, livs)}
							>
								Imprimer
							</button>
						)}
						{isBoss && deps.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => setOpenTotal(true)}
							>
								Totaux
							</button>
						)}
						<button
							type="button"
							style={gs.btnPrimary}
							onClick={() => history.push("/depannage/rechercher")}
						>
							Modifier la recherche
						</button>
					</div>
				</div>

				{deps.length === 0 ? (
					<div style={tableStyles.container}>
						<div style={styles.empty}>Aucun dépannage trouvé</div>
					</div>
				) : (
					<>
						<h2 style={styles.sectionTitle}>Dépannages</h2>
						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th style={tableStyles.th}>N°</th>
										<SortableTh label="Date" colKey="date" sort={sort} onSort={onSort} />
										<th style={tableStyles.th}>Heure</th>
										<SortableTh label="Dépanneur" colKey="depanneur" sort={sort} onSort={onSort} />
										<SortableTh label="Nom" colKey="nom" sort={sort} onSort={onSort} />
										<SortableTh label="Appel" colKey="appel" sort={sort} onSort={onSort} />
										<th style={tableStyles.th}>Lieu</th>
										<th style={tableStyles.th}>Destination</th>
										<th style={tableStyles.th}>Marque</th>
										<th style={tableStyles.th}>Plaques</th>
										<SortableTh label="Montant" colKey="total" sort={sort} onSort={onSort} right />
										<th style={{ ...tableStyles.th, textAlign: "right" }}>Outils</th>
									</tr>
								</thead>
								<tbody>
									{sortedDeps.map((dep, index) => (
										<DepRow
											key={`dep${dep.id}_${index}`}
											dep={dep}
											index={index}
											montant={depMontant(dep)}
											isBoss={isBoss}
											tools={rowTools(dep)}
											lockedBy={lockedBy(dep.id)}
											isOpen={isDerniereVisitee(dep.id)}
											onDoubleClick={() =>
												openFiche(dep.id, sortedDeps)
											}
										/>
									))}
								</tbody>
							</table>
						</div>

						{livs.length > 0 && (
							<>
								<h2 style={styles.sectionTitle}>Livraisons</h2>
								<div style={tableStyles.container}>
									<table style={tableStyles.table}>
										<thead>
											<tr>
												<th style={tableStyles.th}>N°</th>
												<th style={tableStyles.th}>Date</th>
												<th style={tableStyles.th}>Heure</th>
												<th style={tableStyles.th}>Livré par</th>
												<th style={tableStyles.th}>Nom</th>
												<th style={tableStyles.th}>Appel</th>
												<th style={tableStyles.th}>Départ livraison</th>
												<th style={tableStyles.th}>Destination livraison</th>
												<th style={tableStyles.th}>Marque</th>
												<th style={tableStyles.th}>Plaques</th>
												<th style={tableStyles.thRight}>Montant</th>
												<th style={{ ...tableStyles.th, textAlign: "right" }}>Outils</th>
											</tr>
										</thead>
										<tbody>
											{livs.map((dep, index) => (
												<LivRow
													key={`liv${dep.id}_${index}`}
													dep={dep}
													index={index}
													isBoss={isBoss}
													tools={rowTools(dep)}
													lockedBy={lockedBy(dep.id)}
													isOpen={isDerniereVisitee(
														dep.id,
													)}
													onDoubleClick={() =>
														openFiche(dep.id, livs)
													}
												/>
											))}
										</tbody>
									</table>
								</div>
							</>
						)}
					</>
				)}
			</div>

			{openTotal && (
				<DepSearchTotal
					closeModal={() => setOpenTotal(false)}
					deps={deps}
					livs={livs}
				/>
			)}
			{modalDelete && (
				<ResultDeleteModal
					label={modalDelete.title}
					status={deleteState}
					onConfirm={doDelete}
					onClose={closeDelete}
				/>
			)}
		</div>
	)
}

const styles = {
	shell: {
		width: "100%",
		maxWidth: 1400,
		margin: "0 auto",
	},
	headerActions: {
		display: "flex",
		gap: 10,
		alignItems: "center",
	},
	sectionTitle: {
		fontSize: 15,
		fontWeight: 600,
		color: COLORS.text,
		margin: "24px 0 12px",
	},
	thInner: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
	},
	sortBtn: (active, asc) => ({
		border: "none",
		background: "transparent",
		padding: 2,
		cursor: "pointer",
		fontSize: 11,
		color: active ? COLORS.text : COLORS.textLight,
		opacity: active ? 1 : 0.4,
		transform: active && asc ? "rotate(180deg)" : "none",
		transition: "transform 0.15s, opacity 0.12s, color 0.12s",
		lineHeight: 1,
	}),
	td: {
		...tableStyles.td,
		fontVariantNumeric: "tabular-nums",
	},
	toolsCell: {
		display: "flex",
		gap: 6,
		justifyContent: "flex-end",
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

export default DepResultatsV2
